import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Board from 'react-trello';
import { toast } from "sonner";
import { i18n } from "../../translate/i18n";
import { useHistory } from 'react-router-dom';
import { socketConnection } from "../../services/socket";
import TagModal from "../../components/TagModal";
import { Box, Button, Typography } from "@material-ui/core";
import MainContainer from '../../components/MainContainer';
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 3),
    height: "100%",
    overflow: "hidden",
  },
  boardWrapper: {
    flex: 1,
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    maxWidth: "100%",
    marginTop: theme.spacing(2),
    "& .react-trello-board": {
      height: "100%",
      minWidth: "fit-content",
    },
    "& .react-trello-lane": {
      minWidth: 280,
      maxWidth: 280,
      borderRadius: 12,
      marginRight: theme.spacing(2),
      backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#FFFFFF",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    },
    "& .react-trello-lane-header": {
      padding: theme.spacing(1.5, 2),
      borderRadius: "12px 12px 0 0",
      fontFamily: '"Inter", "Poppins", sans-serif',
      fontWeight: 600,
      fontSize: 14,
    },
    "& .react-trello-card": {
      borderRadius: 10,
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#FFFFFF",
      border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      fontFamily: '"Inter", "Poppins", sans-serif',
      "&:hover": {
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      },
    },
    "& .react-trello-card-title": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      fontWeight: 600,
      fontSize: 14,
      color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
      marginBottom: theme.spacing(1),
    },
    "& .react-trello-card-content": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      fontSize: 13,
      color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
      lineHeight: 1.5,
      "& p": {
        margin: theme.spacing(0.5, 0),
      },
    },
  },
  cardButton: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 8,
    padding: "6px 16px",
    backgroundColor: "#0B3C84",
    color: "#FFFFFF",
    marginTop: theme.spacing(1),
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#082D63",
      boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
    },
  },
  addButton: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 20px",
    backgroundColor: "#0B3C84",
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#082D63",
      boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();

  const [tags, setTags] = useState([]);
  const [reloadData, setReloadData] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);

  const [file, setFile] = useState({
    lanes: []
  });

  const [tickets, setTickets] = useState([]);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;
  const jsonString = user.queues.map(queue => queue?.UserQueue?.queueId);

  const fetchTags = async () => {
    try {
      const response = await api.get("/tags/kanban");
      const fetchedTags = response.data.lista || [];

      setTags(fetchedTags);

      // Fetch tickets after fetching tags
      await fetchTickets(jsonString);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTickets = async (jsonString) => {
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: {
          queueIds: JSON.stringify(jsonString),
          teste: true
        }
      });
      setTickets(data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  const popularCards = (jsonString) => {
    const filteredTickets = tickets.filter(ticket => ticket.tags.length === 0);

    const lanes = [
      {
        id: "lane0",
        title: i18n.t("tagsKanban.laneDefault"),
        label: "0",
        cards: filteredTickets.map(ticket => ({
          id: ticket.id.toString(),
          label: "Ticket nº " + ticket.id.toString(),
          description: (
            <div>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                {ticket.contact.number}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 12, color: "#64748B" }}>
                {ticket.lastMessage}
              </Typography>
              <Button
                className={classes.cardButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick(ticket.uuid);
                }}
              >
                Ver Ticket
              </Button>
            </div>
          ),
          title: `${ticket.contact.name} #${ticket.id}`,
          draggable: true,
          href: "/tickets/" + ticket.uuid,
        })),
      },
      ...tags.map(tag => {
        const filteredTickets = tickets.filter(ticket => {
          const tagIds = ticket.tags.map(tag => tag.id);
          return tagIds.includes(tag.id);
        });

        return {
          id: tag.id.toString(),
          title: tag.name,
          label: tag.id.toString(),
          cards: filteredTickets.map(ticket => ({
            id: ticket.id.toString(),
            label: "Ticket nº " + ticket.id.toString(),
            description: (
              <div>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  {ticket.contact.number}
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 12, color: "#64748B" }}>
                  {ticket.lastMessage}
                </Typography>
                <Button
                  className={classes.cardButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(ticket.uuid);
                  }}
                >
                  Ver Ticket
                </Button>
              </div>
            ),
            title: `${ticket.contact.name} #${ticket.id}`,
            draggable: true,
            href: "/tickets/" + ticket.uuid,
          })),
          style: { 
            backgroundColor: tag.color, 
            color: "white",
            borderRadius: "12px 12px 0 0",
          }
        };
      }),
    ];

    setFile({ lanes });
  };

  const handleCardClick = (uuid) => {
    history.push('/tickets/' + uuid);
  };

  useEffect(() => {
    popularCards(jsonString);
  }, [tags, tickets, reloadData]);

  const handleCardMove = async (cardId, sourceLaneId, targetLaneId) => {
    try {
      await api.delete(`/ticket-tags/${targetLaneId}`);
      toast.success('Ticket Tag Removido!');
      await api.put(`/ticket-tags/${targetLaneId}/${sourceLaneId}`);
      toast.success('Ticket Tag Adicionado com Sucesso!');
    } catch (err) {
      console.log(err);
    }
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  return (
    <>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        reload={fetchTags}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
        kanban={1}
      />

      <MainContainer>
        <Box className={classes.container}>
          <MainHeader>
            <Title>Kanban</Title>
            <MainHeaderButtonsWrapper>
              <Button
                variant="contained"
                className={classes.addButton}
                onClick={() => setTagModalOpen(true)}
              >
                Adicionar lane
              </Button>
            </MainHeaderButtonsWrapper>
          </MainHeader>

          <Box className={classes.boardWrapper}>
            <Board
              data={file}
              onCardMoveAcrossLanes={handleCardMove}
              style={{ backgroundColor: 'transparent', height: '100%' }}
            />
          </Box>
        </Box>
      </MainContainer>
    </>
  );
};

export default Kanban;
